import { persist } from "zustand/middleware";
import { create } from "zustand";
import { createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Asset, AssetTemplate } from "../generated/gql-types";
import { assetActions } from "./asset.action";

interface AssetStore {
  assets: Asset[];
  assetTemplates: AssetTemplate[];
  setAssets: (assets: Asset[]) => void;
  getAssetsForUser: (userId: string) => Asset[];
  getAssetTemplatesForTenant: (tenantId: string) => AssetTemplate[];
  fetchAssetsForUser: (userId: string) => Promise<Asset[]>;
  fetchAssetTemplatesForTenant: (tenantId: string) => Promise<AssetTemplate[]>;
}

export const useAssetStore = create<AssetStore>()(
  persist(
    (set, get) => ({
      assets: [],
      assetTemplates: [],
      setAssets: (assets: Asset[]) => set({ assets }),
      getAssetsForUser: (userId: string) => {
        const filteredAssets = get().assets.filter(
          (asset) => asset.userId === userId
        );
        return filteredAssets;
      },
      getAssetTemplatesForTenant: (tenantId: string) => {
        const filteredAssetTemplates = get().assetTemplates.filter(
          (assetTemplate) => assetTemplate.tenantId === tenantId
        );
        return filteredAssetTemplates;
      },
      fetchAssetsForUser: async (userId: string) => {
        try {
          const newAssets = await assetActions.fetchAssetsForUser(userId);

          // Replace this user's assets with fresh data (URLs expire ~12h).
          // Keep assets for other users.
          const existingAssets = get().assets;
          const otherUsersAssets = existingAssets.filter(
            (asset: Asset) => asset.userId !== userId
          );

          set({ assets: [...otherUsersAssets, ...newAssets] });
          return newAssets;
        } catch (error) {
          console.error("Error fetching assets for user:", error);
          throw error;
        }
      },
      fetchAssetTemplatesForTenant: async (tenantId: string) => {
        try {
          const assetTemplates =
            await assetActions.fetchAssetTemplatesForTenant(tenantId);
          set({ assetTemplates });
          return assetTemplates;
        } catch (error) {
          console.error("Error fetching asset templates for tenant:", error);
          throw error;
        }
      },
    }),
    {
      name: "asset-store",
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        assets: state.assets,
        assetTemplates: state.assetTemplates,
      }),
    }
  )
);
